if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            // Extract and set product variants data for the modal
            this.extractProductVariants(responseHTML);

            // Initialize enhanced variant picker functionality
            this.initializeEnhancedVariantPicker();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Remove image gallery and media elements from modal
        const mediaGallery = productElement.querySelector('.main-product__media-gallery');
        if (mediaGallery) mediaGallery.remove();

        const mediaSection = productElement.querySelector('.main-product__media');
        if (mediaSection) mediaSection.remove();

        const productMedia = productElement.querySelector('.product__media');
        if (productMedia) productMedia.remove();

        const swiperElements = productElement.querySelectorAll('.swiper, .swiper-container');
        swiperElements.forEach(el => el.remove());

        // Remove thumbnails
        const thumbnails = productElement.querySelector('.main-product__media-thumbnails');
        if (thumbnails) thumbnails.remove();

        // Remove any other media-related elements
        const mediaItems = productElement.querySelectorAll('.product__media-item, .product__media-list');
        mediaItems.forEach(el => el.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      extractProductVariants(responseHTML) {
        // Try to extract product variants from the script tag in the response
        const scriptTags = responseHTML.querySelectorAll('script');
        for (const script of scriptTags) {
          const content = script.textContent || script.innerHTML;
          if (content.includes('window.productVariants')) {
            try {
              // Extract the variants data
              const match = content.match(/window\.productVariants\s*=\s*(\[.*?\]);/s);
              if (match) {
                this.modalProductVariants = JSON.parse(match[1]);
                // Also set it globally for the modal context
                if (!window.productVariants) {
                  window.productVariants = this.modalProductVariants;
                }
              }
            } catch (error) {
              console.error('Error extracting product variants:', error);
            }
            break;
          }
        }

        // Extract product metafields including discount percentage
        this.extractProductMetafields(responseHTML);
      }

      extractProductMetafields(responseHTML) {
        // Try to extract product metafields from the script tag in the response
        const scriptTags = responseHTML.querySelectorAll('script');
        for (const script of scriptTags) {
          const content = script.textContent || script.innerHTML;
          if (content.includes('window.productMetafields')) {
            try {
              // Extract the metafields data
              const match = content.match(/window\.productMetafields\s*=\s*(\{.*?\});/s);
              if (match) {
                this.modalProductMetafields = JSON.parse(match[1]);
                // Also set it globally for the modal context
                if (!window.productMetafields) {
                  window.productMetafields = this.modalProductMetafields;
                }
              }
            } catch (error) {
              console.error('Error extracting product metafields:', error);
            }
            break;
          }
        }
      }

      initializeEnhancedVariantPicker() {
        const modalContent = this.modalContent;
        if (!modalContent) return;

        // Get product variants data from the loaded content
        const productInfo = modalContent.querySelector('product-info');
        if (!productInfo) return;

        // Initialize variant picker functionality
        this.setupVariantPickerEvents(modalContent);

        // Listen for quantity changes (let Dawn theme handle the buttons)
        this.setupQuantityListener(modalContent);

        // Initial price and total calculation
        this.updateModalPriceAndTotal(modalContent);
      }

      setupVariantPickerEvents(modalContent) {
        const variantSelects = modalContent.querySelector('variant-selects');
        const variantIdInput = modalContent.querySelector('input[name="id"]');

        if (!variantSelects || !variantIdInput) return;

        // Listen for changes to the variant ID input (same as main product page)
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
              this.updateModalPriceAndTotal(modalContent);
            }
          });
        });

        observer.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });

        // Also listen for change events on variant ID input
        variantIdInput.addEventListener('change', () => {
          this.updateModalPriceAndTotal(modalContent);
        });

        // Listen for changes on all variant option inputs (radio buttons, selects)
        const variantOptions = variantSelects.querySelectorAll('input[type="radio"], select');
        variantOptions.forEach((option) => {
          option.addEventListener('change', () => {
            // Small delay to allow Dawn theme to update the variant ID input
            setTimeout(() => {
              this.updateModalPriceAndTotal(modalContent);
            }, 100);
          });
        });

        // Listen for variant-selects change event
        variantSelects.addEventListener('change', () => {
          setTimeout(() => {
            this.updateModalPriceAndTotal(modalContent);
          }, 50);
        });
      }

      setupQuantityListener(modalContent) {
        // Only listen for quantity input changes, don't interfere with buttons
        const quantityInput = modalContent.querySelector('input[name="quantity"], .quantity-number');
        if (!quantityInput) return;

        // Listen for input changes (when user types or when Dawn theme updates the value)
        quantityInput.addEventListener('input', () => {
          // Update total when quantity changes
          this.updateModalTotalOnly(modalContent);
        });

        // Listen for change events as well
        quantityInput.addEventListener('change', () => {
          this.updateModalTotalOnly(modalContent);
        });
      }

      updateModalTotalOnly(modalContent) {
        // Just update the total without changing prices or other elements
        const quantityInput = modalContent.querySelector('input[name="quantity"], .quantity-number');
        const totalElement = modalContent.querySelector('.total-amount');
        const variantIdInput = modalContent.querySelector('input[name="id"]');

        if (!quantityInput || !totalElement || !variantIdInput) return;

        const quantity = parseInt(quantityInput.value) || 1;
        const variantId = variantIdInput.value;

        // Get current variant
        let variant = null;
        const selectedVariantData = modalContent.querySelector('[data-selected-variant]');
        if (selectedVariantData) {
          try {
            variant = JSON.parse(selectedVariantData.innerHTML);
          } catch (error) {
            if (window.productVariants) {
              variant = window.productVariants.find(v => v.id == variantId);
            }
          }
        } else if (window.productVariants) {
          variant = window.productVariants.find(v => v.id == variantId);
        }

        if (variant && variant.price) {
          const total = quantity * (variant.price / 100);
          totalElement.textContent = `₹${total.toFixed(0)}`;
        }
      }



      updateModalPriceAndTotal(modalContent) {
        // Get the variant ID input (this is what Dawn theme updates)
        const variantIdInput = modalContent.querySelector('input[name="id"]');
        const quantityInput = modalContent.querySelector('input[name="quantity"], .quantity-number');

        if (!variantIdInput || !quantityInput) return;

        const variantId = variantIdInput.value;
        if (!variantId) return;

        // Get variant data from the selected variant data or try to find it
        let variant = null;

        // First try to get from the data-selected-variant element
        const selectedVariantData = modalContent.querySelector('[data-selected-variant]');
        if (selectedVariantData) {
          try {
            variant = JSON.parse(selectedVariantData.innerHTML);
          } catch (error) {
            console.error('Error parsing variant data:', error);
          }
        }

        // If we have window.productVariants, use that as backup
        if (!variant && window.productVariants) {
          variant = window.productVariants.find(v => v.id == variantId);
        }

        if (!variant) return;

        // Update price display using the same logic as main product page
        this.updateModalPrice(modalContent, variant);

        // Update total display using the same logic as main product page
        this.updateModalTotal(modalContent, variant);

        // Update add to cart button
        this.updateModalAddToCartButton(modalContent, variant);

        // Update quantity rules
        this.updateModalQuantityRules(modalContent, variant);
      }

      updateModalPrice(modalContent, variant) {
        // Find price elements - same structure as price snippet
        const regularPriceElement = modalContent.querySelector('.price__regular .price-item--regular');
        const salePriceElement = modalContent.querySelector('.price__sale .price-item--sale');
        const comparePriceElement = modalContent.querySelector('.price__sale .price-item--regular');
        const priceContainer = modalContent.querySelector('.price');

        if (!variant || !priceContainer) return;

        // Apply the same discount logic as in price.liquid
        let price = variant.price;
        let compareAtPrice = variant.compare_at_price;
        let originalPrice = price;
        let shouldShowSalePrice = false;

        // Check if current variant price is greater than 150 and not already discounted
        // This ensures the discount only applies to specific variants, not the entire product
        if (price > 100 && compareAtPrice == null) {
          // Get discount percentage from product metafields
          const productInfo = modalContent.querySelector('product-info');
          if (productInfo) {
            const productId = productInfo.dataset.productId;
            // Try to get discount from the product data
            const discountPercentage = this.getDiscountPercentage(modalContent, productId);

            if (discountPercentage && discountPercentage > 0) {
              const discountAmount = Math.floor(price * discountPercentage / 100);
              const discountedPrice = price - discountAmount;
              compareAtPrice = price;
              price = discountedPrice;
              shouldShowSalePrice = true;
            }
          }
        } else if (compareAtPrice && compareAtPrice > price) {
          // Already has a compare at price
          shouldShowSalePrice = true;
        }

        // Format prices using the same format as main product
        const formattedPrice = this.formatPrice(price);
        const formattedComparePrice = compareAtPrice ? this.formatPrice(compareAtPrice) : null;

        // Update price elements
        if (regularPriceElement) {
          regularPriceElement.textContent = formattedPrice;
        }

        if (salePriceElement) {
          salePriceElement.textContent = formattedPrice;
        }

        if (comparePriceElement && formattedComparePrice) {
          comparePriceElement.textContent = formattedComparePrice;
        }

        // Show/hide price sections based on sale status
        const regularPriceDiv = modalContent.querySelector('.price__regular');
        const salePriceDiv = modalContent.querySelector('.price__sale');

        if (shouldShowSalePrice) {
          // Show sale price, hide regular
          if (regularPriceDiv) regularPriceDiv.style.display = 'none';
          if (salePriceDiv) salePriceDiv.style.display = 'block';

          // Add sale class to price container to ensure CSS rules apply
          priceContainer.classList.add('price--on-sale');
          priceContainer.classList.remove('price--sold-out');
        } else {
          // Show regular price, hide sale
          if (regularPriceDiv) regularPriceDiv.style.display = 'block';
          if (salePriceDiv) salePriceDiv.style.display = 'none';

          // Remove sale class
          priceContainer.classList.remove('price--on-sale');
        }

        // Handle sold out state
        if (!variant.available) {
          priceContainer.classList.add('price--sold-out');
        } else {
          priceContainer.classList.remove('price--sold-out');
        }
      }

      updateModalTotal(modalContent, variant) {
        const quantityInput = modalContent.querySelector('input[name="quantity"], .quantity-number');
        const totalElement = modalContent.querySelector('.total-amount');

        if (!quantityInput || !totalElement || !variant) return;

        const quantity = parseInt(quantityInput.value) || 1;

        // Apply the same discount logic as in price.liquid
        let price = variant.price;
        let compareAtPrice = variant.compare_at_price;

        // Check if current variant price is greater than 150 and not already discounted
        if (price > 100 && compareAtPrice == null) {
          const productInfo = modalContent.querySelector('product-info');
          if (productInfo) {
            const productId = productInfo.dataset.productId;
            const discountPercentage = this.getDiscountPercentage(modalContent, productId);

            if (discountPercentage && discountPercentage > 0) {
              const discountAmount = Math.floor(price * discountPercentage / 100);
              price = price - discountAmount;
            }
          }
        }

        const total = quantity * (price / 100);
        totalElement.textContent = `₹${total.toFixed(0)}`;
      }

      updateModalAddToCartButton(modalContent, variant) {
        const addToCartButton = modalContent.querySelector('button[name="add"]');
        if (!addToCartButton || !variant) return;

        const buttonText = addToCartButton.querySelector('span');

        if (variant.available) {
          addToCartButton.disabled = false;
          addToCartButton.removeAttribute('disabled');
          addToCartButton.removeAttribute('aria-disabled');
          if (buttonText) {
            buttonText.textContent = 'Add to cart';
          }
        } else {
          addToCartButton.disabled = true;
          addToCartButton.setAttribute('disabled', 'disabled');
          addToCartButton.setAttribute('aria-disabled', 'true');
          if (buttonText) {
            buttonText.textContent = 'Sold out';
          }
        }
      }

      updateModalQuantityRules(modalContent, variant) {
        const quantityInput = modalContent.querySelector('input[name="quantity"], .quantity-number');
        if (!quantityInput || !variant || !variant.quantity_rule) return;

        const minValue = variant.quantity_rule.min || 1;
        let maxValue = variant.quantity_rule.max;
        const stepValue = variant.quantity_rule.increment || 1;

        quantityInput.setAttribute('min', minValue);
        quantityInput.setAttribute('data-min', minValue);
        quantityInput.setAttribute('step', stepValue);

        if (maxValue && maxValue > 5) maxValue = 5;
        if (!maxValue) maxValue = 5;

        if (maxValue) {
          quantityInput.setAttribute('max', maxValue);
          quantityInput.setAttribute('data-max', maxValue);
        } else {
          // Fallback if somehow still null
          quantityInput.setAttribute('max', 5);
          quantityInput.setAttribute('data-max', 5);
        }

        // Update current value if it's below minimum
        const currentValue = parseInt(quantityInput.value) || 1;
        if (currentValue < minValue) {
          quantityInput.value = minValue;
        }
      }





      formatPrice(priceInCents) {
        try {
          return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(priceInCents / 100);
        } catch (error) {
          // Fallback formatting
          const price = (priceInCents / 100).toFixed(0);
          return `₹${price}`;
        }
      }

      getDiscountPercentage(modalContent, productId) {
        // First try to get from the extracted product metafields
        if (window.productMetafields && window.productMetafields.custom && window.productMetafields.custom.discount_percentage_decimal) {
          try {
            return parseFloat(window.productMetafields.custom.discount_percentage_decimal);
          } catch (error) {
            console.error('Error parsing discount percentage:', error);
          }
        }

        // Try to get discount percentage from product data
        // First check if we have product data in the modal
        const productDataElement = modalContent.querySelector('[data-product-json]');
        if (productDataElement) {
          try {
            const productData = JSON.parse(productDataElement.innerHTML);
            if (productData && productData.metafields && productData.metafields.custom && productData.metafields.custom.discount_percentage_decimal) {
              return parseFloat(productData.metafields.custom.discount_percentage_decimal);
            }
          } catch (error) {
            console.error('Error parsing product data:', error);
          }
        }

        // Fallback: try to get from window.productVariants if available
        if (window.productVariants && window.productVariants.length > 0) {
          // Find a variant from this product and try to get the discount
          const variant = window.productVariants.find(v => v.product_id == productId);
          if (variant && variant.discount_percentage) {
            return parseFloat(variant.discount_percentage);
          }
        }

        // As a last resort, try to extract from the HTML content
        const priceElement = modalContent.querySelector('.price');
        if (priceElement) {
          // Look for data attributes or hidden elements that might contain the discount
          const discountAttr = priceElement.dataset.discountPercentage;
          if (discountAttr) {
            try {
              return parseFloat(discountAttr);
            } catch (error) {
              console.error('Error parsing discount attribute:', error);
            }
          }
        }

        return null;
      }
    }
  );
}
